// Papelera de clientes — devolver lo que se borró por error.
//
// Borrar un cliente en Deskte es SUAVE: la ficha no se destruye, se marca con
// una fecha de borrado, y sus pólizas y comisiones se marcan con LA MISMA
// fecha en el mismo movimiento. Es decir, el dato siempre estuvo ahí… pero no
// existía ninguna pantalla para devolverlo, así que en la práctica era una
// pérdida definitiva.
//
// Lo que forzó esta ventana: en agosto de 2026 dos agencias (Allinsure y
// Marianela) importaron su cartera, se les llenó "Prospectos y Referidos" con
// todos sus clientes, y al intentar limpiarlo pulsaron una papelera que decía
// "se quita de tu lista" y borraba al cliente entero con sus pólizas y sus
// comisiones detrás (E-092). El botón ya está arreglado; esto es lo que
// devuelve lo que se perdió mientras estuvo mal.
//
// Regla de la restauración, y es la parte delicada: vuelve el cliente y SOLO
// lo que cayó en ese mismo borrado. Una póliza que alguien dio de baja a mano
// tres semanas antes se queda de baja — resucitarla sería un error peor que el
// que se está reparando.
import { useEffect, useState } from 'react';
import { Trash2, RotateCcw, ShieldCheck } from 'lucide-react';
import { Modal, confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { listDeletedClients, restoreClient } from '../api.js';
import { IS_DEMO } from '../demoMode.js';

// Fecha y hora en formato de EE. UU. y en hora LOCAL. `toISOString()` correría
// el día en cualquier zona al oeste de UTC: un borrado de las 8 de la noche
// aparecería como del día siguiente.
function fmtStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const DIAS = [30, 90, 180, 365];

export default function ClientsTrashModal({ lang = 'es', onClose, onRestored }) {
  const es = lang === 'es';
  const [dias, setDias] = useState(90);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    // La demo pública no tiene servidor: apiFetch rechaza a propósito y sin
    // esta salida la ventana se abriría con un error rojo en la cara del
    // visitante. Una papelera vacía es la verdad allí.
    if (IS_DEMO) { setRows([]); return undefined; }
    let vivo = true;
    setRows(null); setError(null);
    listDeletedClients(dias)
      .then((r) => { if (vivo) setRows(r); })
      .catch((e) => { if (vivo) setError(e.message || String(e)); });
    return () => { vivo = false; };
  }, [dias]);

  const nombre = (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || (es ? '(sin nombre)' : '(no name)');

  const devolver = async (r) => {
    const arrastra = [];
    if (r.policiesCount) arrastra.push(es ? `${r.policiesCount} póliza(s)` : `${r.policiesCount} polic${r.policiesCount === 1 ? 'y' : 'ies'}`);
    if (r.commissionsCount) arrastra.push(es ? `${r.commissionsCount} comisión(es)` : `${r.commissionsCount} commission(s)`);
    const ok = await confirmDialog({
      title: es ? '¿Devolver este cliente?' : 'Restore this client?',
      message: es
        ? `${nombre(r)} vuelve al módulo de Clientes${arrastra.length ? `, junto con ${arrastra.join(' y ')}` : ''}. Solo vuelve lo que se borró en el mismo momento que la ficha.`
        : `${nombre(r)} returns to the Clients module${arrastra.length ? `, along with ${arrastra.join(' and ')}` : ''}. Only what was deleted at the same moment as the record comes back.`,
      confirmLabel: es ? 'Devolver' : 'Restore',
    });
    if (!ok) return;
    setBusyId(r.id);
    try {
      const res = await restoreClient(r.id);
      setRows((prev) => (prev || []).filter((x) => x.id !== r.id));
      const extra = [];
      if (res.policiesRestored) extra.push(es ? `${res.policiesRestored} póliza(s)` : `${res.policiesRestored} policies`);
      if (res.commissionsRestored) extra.push(es ? `${res.commissionsRestored} comisión(es)` : `${res.commissionsRestored} commissions`);
      toast.success(es
        ? `${nombre(r)} volvió${extra.length ? ` con ${extra.join(' y ')}` : ''}.`
        : `${nombre(r)} restored${extra.length ? ` with ${extra.join(' and ')}` : ''}.`);
      // La lista de clientes de la pantalla de atrás no sabe nada de esto: se
      // le pide que se recargue, o el cliente devuelto no aparece hasta el
      // siguiente refresco de la página.
      if (onRestored) onRestored(res);
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo devolver.' : 'Could not restore.'));
    } finally {
      setBusyId(null);
    }
  };

  const cell = { padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', borderBottom: '1px solid var(--c-border,#1a3550)' };
  const head = { ...cell, fontSize: '.64rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 };

  return (
    <Modal title={es ? 'Papelera de clientes' : 'Client trash'} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
        <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.55, display: 'flex', gap: '.55rem', alignItems: 'flex-start' }}>
          <ShieldCheck size={16} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0, marginTop: 2 }} />
          <span>
            {es
              ? 'Al borrar un cliente su ficha no se destruye: se guarda aquí con sus pólizas y comisiones. Devolverlo lo trae de vuelta tal como estaba.'
              : 'Deleting a client does not destroy the record: it is kept here with their policies and commissions. Restoring brings it all back as it was.'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>
            {es ? 'Borrados en los últimos:' : 'Deleted in the last:'}
          </span>
          {DIAS.map((d) => (
            <button key={d} type="button" onClick={() => setDias(d)}
              style={{
                padding: '.24rem .6rem', borderRadius: 100, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: '.72rem', fontWeight: 700,
                border: `1px solid ${dias === d ? 'rgba(0,212,255,.45)' : 'var(--c-border,#1a3550)'}`,
                background: dias === d ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'transparent',
                color: dias === d ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)',
              }}>
              {d} {es ? 'días' : 'days'}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{error}</div>
        )}

        {rows === null && !error && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1.5rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>
        )}

        {rows && rows.length === 0 && !error && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.5rem' }}>
            <Trash2 size={22} style={{ opacity: .6 }} />
            {es ? 'No hay clientes borrados en este período.' : 'No clients deleted in this period.'}
          </div>
        )}

        {rows && rows.length > 0 && (
          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Cliente' : 'Client'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Se borró' : 'Deleted'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Quién' : 'Who'}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{es ? 'Vuelve con' : 'Comes back with'}</th>
                  <th style={{ ...head, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...cell, fontWeight: 600 }}>{nombre(r)}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '.72rem' }}>{fmtStamp(r.deletedAt)}</td>
                    <td style={{ ...cell, color: 'var(--c-text2,#7aa3c0)' }}>{r.deletedByName || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', whiteSpace: 'nowrap' }}>
                      {r.policiesCount || r.commissionsCount
                        ? [
                            r.policiesCount ? `${r.policiesCount} ${es ? 'póliza(s)' : 'policies'}` : null,
                            r.commissionsCount ? `${r.commissionsCount} ${es ? 'comisión(es)' : 'commissions'}` : null,
                          ].filter(Boolean).join(' · ')
                        : (es ? 'solo la ficha' : 'record only')}
                    </td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <button type="button" className="dkt-btn-g xs" onClick={() => devolver(r)} disabled={busyId === r.id}
                        style={{ whiteSpace: 'nowrap' }}>
                        <RotateCcw size={12} /> {busyId === r.id ? (es ? 'Devolviendo…' : 'Restoring…') : (es ? 'Devolver' : 'Restore')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows && rows.length > 0 && (
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? '«Vuelve con» cuenta solo lo que se borró en el mismo momento que la ficha. Lo que alguien dio de baja por separado sigue de baja.'
              : '“Comes back with” counts only what was deleted at the same moment as the record. Anything removed separately stays removed.'}
          </div>
        )}
      </div>
    </Modal>
  );
}
